import React, { useState, useEffect, useMemo, useRef } from "react";
import { FaCheckCircle } from "react-icons/fa";
import Dialog from "../../dialogs"; // Ensure this path is correct
import {
  CheckIcon,
  InformationCircleIcon,
  XMarkIcon,
} from "@heroicons/react/16/solid";
import { baseAxios } from "../../../apis";
import { useCsrfToken } from "../../../hooks/useCsrfToken";
import { useLocation, useNavigate } from "react-router-dom";
import DialogAsk from "../../dialogs/DialogAsk";
import useAuthedApi from "../../../hooks/useAuthedApi";
import { IconSystemAlert } from "../../../assets/icons/IconSystemAlert";
import { useCourseNavigator } from "../../../hooks/useCourseNavigator";
import CourseNavBar from "../components/CourseNavbar";
import {
  useCourseDetailQuery,
  useCoursesQuery,
} from "../../../hooks/useCourse";

// This function transforms the API data into the format the component expects.
const transformExamData = (examDetail) => {
  if (!examDetail || !examDetail.questions) return [];
  return examDetail.questions.map((q) => {
    const options = {};
    let correctAnswer = null;
    q.options.forEach((opt) => {
      options[opt.id] = opt.text;
      if (opt.is_correct) {
        correctAnswer = opt.id;
      }
    });
    return {
      id: q.id,
      lesson: `${examDetail.category_name} - ${examDetail.level_name}: Question ${q.order}`, // Placeholder as lesson is not in new data
      questionText: q.text,
      options,
      correctAnswer,
      explanation: "Explanation is not provided in the new data structure.", // Placeholder
    };
  });
};

export default function ExamCourse({ examDetail, loading }) {
  // --- STATE MANAGEMENT ---
  const { authedPost } = useAuthedApi();
  const [examState, setExamState] = useState("in-progress"); // 'in-progress', 'submitting', 'submitted'
  const [answers, setAnswers] = useState({});
  const [isHeaderScroll, setIsHeaderScroll] = useState(false);
  const headerRef = useRef(null);
  const scrollContainerRef = useRef(null); // <-- ADD THIS LINE
  const sentinelRef = useRef(null); // <-- ADD THIS
  const navigate = useNavigate();
  const location = useLocation();
  const [openDialog, setOpenDialog] = useState(null);

  // alrt
  const [unansweredAlert, setUnansweredAlert] = useState(null);
  const [isNoAnswerQuestion, setIsNoAnswerQuestion] = useState(false);
  const alertRef = useRef(null);

  const { data: coursesDetail, isLoading: loadingCourseDetails } =
    useCourseDetailQuery(examDetail?.course);
  const { data: courses = [], isLoading: loadingCourses } = useCoursesQuery();

  const { prevItem, nextItem, isEndOfCourse, nextCourseId } =
    useCourseNavigator({
      coursesDetail: coursesDetail || { contents: [] },
      currentId: examDetail?.id || "",
      allCourses: courses || [],
      currentKind: "lesson",
      currentCourseId: examDetail?.course || "",
    });

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    const sentinel = sentinelRef.current;

    if (!scrollContainer || !sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsHeaderScroll(!entry.isIntersecting);
      },
      { root: scrollContainer }
    );

    requestAnimationFrame(() => {
      observer.observe(sentinel);
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  // Use examDetail duration if available, otherwise default to 0.
  // The duration from JSON is in minutes, so we convert it to seconds.
  const totalTime = useMemo(
    () => (examDetail?.duration || 0) * 60,
    [examDetail]
  );
  const [timeLeft, setTimeLeft] = useState(totalTime);

  // Update timeLeft if totalTime changes (e.g., when examDetail loads)
  useEffect(() => {
    setTimeLeft(totalTime);
  }, [totalTime]);

  // Transform the raw exam data from props into a more usable format
  const questions = useMemo(() => transformExamData(examDetail), [examDetail]);

  // --- TIMER AND NAVIGATION BLOCKER LOGIC ---
  // useEffect(() => {
  //   if (examState !== "in-progress" || timeLeft <= 0) {
  //     if (timeLeft <= 0) handleSubmit();
  //     return;
  //   }
  //   const timer = setInterval(() => setTimeLeft((prev) => prev - 1), 1000);
  //   return () => clearInterval(timer);
  // }, [timeLeft, examState]);

  // Effect for preventing tab close/refresh
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue =
        "Are you sure you want to leave? Your progress will be lost.";
    };

    const handlePopState = (e) => {
      if (examState === "in-progress") {
        const confirmLeave = window.confirm(
          "Are you sure you want to go back? Your progress will be lost."
        );
        if (!confirmLeave) {
          navigate(location.pathname); // Prevent leaving
        }
      }
    };

    // --- React Router navigation blocker ---
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    const overrideHistory = (method) => {
      return function (...args) {
        if (examState === "in-progress") {
          const confirmLeave = window.confirm(
            "Are you sure you want to leave? Your progress will be lost."
          );
          if (!confirmLeave) return;
        }
        return method.apply(this, args);
      };
    };

    if (examState === "in-progress") {
      window.addEventListener("beforeunload", handleBeforeUnload);
      window.addEventListener("popstate", handlePopState);

      window.history.pushState = overrideHistory(originalPushState);
      window.history.replaceState = overrideHistory(originalReplaceState);
    }

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);

      // Restore original history methods
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
    };
  }, [examState, navigate, location]);

  // --- HANDLER FUNCTIONS ---
  const handleAnswerChange = (questionId, optionId) => {
    setAnswers((prev) => ({ ...prev, [questionId]: optionId }));
  };

  useEffect(() => {
    if (loading || examState !== "in-progress" || timeLeft <= 0) {
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          handleSubmit(); // Auto submit when time runs out
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [loading, examState, timeLeft]);

  const handleSubmit = async () => {
    // Check for unanswered questions

    const unanswered = questions
      .map((q, i) => ({ ...q, number: i + 1 }))
      .filter((q) => !answers[q.id])
      .map((q) => q.number);

    if (unanswered.length > 0) {
      setUnansweredAlert(
        `You haven’t answered all the questions yet. Complete question number ${unanswered.join(
          ", "
        )} to finish your exam.`
      );
      alertRef.current?.scrollIntoView({ behavior: "smooth" });
      setIsNoAnswerQuestion(true);
      return;
    }
    setIsNoAnswerQuestion(false);

    // --- existing logic ---
    if (examState !== "in-progress" || !examDetail?.attempt_id) {
      console.error(
        "Submission prevented: Invalid state or missing attempt ID."
      );
      return;
    }

    setExamState("submitting");

    try {
      const formattedAnswers = Object.entries(answers).map(
        ([question_id, selected_option_id]) => ({
          question_id: parseInt(question_id, 10),
          selected_option_id,
        })
      );

      const response = await authedPost(
        `/api/courses/exams/attempts/${examDetail.attempt_id}/submit/`,
        { answers: formattedAnswers }
      );

      console.log("Exam submitted successfully:", response.data);
      setExamState("submitted");
    } catch (error) {
      console.error(
        "Submission failed:",
        error.response?.data || error.message
      );
      setExamState("in-progress");
    }
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(minutes).padStart(2, "0")}:${String(secs).padStart(
      2,
      "0"
    )}`;
  };

  // --- DYNAMIC STYLING ---
  const timerColor = timeLeft <= 299 ? "text-[#B3261E]" : "text-gray-900";

  useEffect(() => {
    if (!unansweredAlert) return;

    // Auto-dismiss after 4 seconds
    // const timer = setTimeout(() => setUnansweredAlert(null), 5000);

    // Handle click outside
    const handleClickOutside = (e) => {
      if (alertRef.current && !alertRef.current.contains(e.target)) {
        setUnansweredAlert(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      // clearTimeout(timer);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [unansweredAlert]);

  // --- RENDER LOGIC ---
  if (loading) {
    return (
      <div className="flex justify-center items-center h-[93vh] bg-[#fff] rounded-xl">
        <div className="text-center">
          <svg
            className="animate-spin h-10 w-10 text-[#007A78] mx-auto"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          <p className="mt-4 text-lg font-semibold text-gray-700">
            Loading your exam...
          </p>
        </div>
      </div>
    );
  }

  if (examState === "submitting") {
    return (
      <div className="flex justify-center items-center h-[93vh] bg-[#fff] rounded-xl">
        <div className="text-center">
          <svg
            className="animate-spin h-10 w-10 text-[#007A78] mx-auto"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          <p className="mt-4 text-lg font-semibold text-gray-700">
            Submitting your exam...
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen ">
        <Dialog
          open={openDialog === "leave-exam"}
          onClose={() => setOpenDialog(null)}
          // footer={dialogFooter}
          className="border-none ring-0"
        >
          <h3 className="text-xl font-bold leading-6 text-gray-800">
            Confirmation
          </h3>
          <p className="text-sm text-gray-700 mt-4 font-normal">
            You're currently participating in an exam. Exiting now will result
            in loss of progress. Do you still wish to leave?
          </p>
          <div className="flex justify-end gap-3 mt-3">
            <div className="flex justify-end gap-3 mt-3">
              <button
                onClick={() => setOpenDialog(null)}
                className="px-4 py-2 text-sm font-medium text-[#B3261E] bg-white ring-1 ring-[#B3261E] rounded-lg hover:bg-red-50"
              >
                Continue Time
              </button>
              <button
                onClick={() => navigate(-1)}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-900 rounded-lg hover:bg-indigo-800"
              >
                Leave Exam
              </button>
            </div>
          </div>
        </Dialog>

        <p className="text-sm font-semibold mb-6">
          <span className="text-[#0E0F11]">
            {examDetail?.category_name} Track{" "}
          </span>
          /{" "}
          <span className="text-[#0E0F11]/60">
            {examDetail?.level_name} Course
          </span>{" "}
          / <span className="text-[#0E0F11]/60">{examDetail?.title}</span>
        </p>
        <div
          ref={scrollContainerRef}
          // className="w-full md:h-[calc(100vh-10rem)] overflow-y-auto no-scrollbar mx-auto bg-white rounded-xl shadow-md overflow-hidden p-6 pt-0"
          className={`w-full md:h-fit ${
            examState === "in-progress"
              ? "md:max-h-[calc(100vh-10rem)]"
              : "md:max-h-[calc(100vh-12rem)]"
          } overflow-y-auto no-scrollbar mx-auto bg-white rounded-xl shadow-md overflow-hidden p-6 pt-0`}
        >
          <div
            ref={headerRef}
            className={`flex flex-col md:flex-row justify-between items-center border-gray-200 sticky top-[0] bg-white ml-[-24px] w-[calc(100%+48px)] p-6 ${
              isHeaderScroll && ""
              // isHeaderScroll && "shadow-[0px_5px_10px_#0002]"
            }`}
          >
            {unansweredAlert && (
              <div
                ref={alertRef}
                className={`
                  absolute left-1/2 -translate-x-1/2 top-4 
                  flex items-start gap-3 
                  bg-[#FAD5D5] text-[#8C0F0F] rounded-lg 
                  px-5 py-3
                  w-[480px]
                  shadow-[0_2px_4px_rgba(0,0,0,0.08),_0_8px_16px_rgba(0,0,0,0.15)]
                  animate-[fadeSlideInExamAlert_0.4s_ease-out_forwards]
                  z-[50] 
                `}
              >
                <IconSystemAlert className="w-[20px] h-[20px] flex-shrink-0 mt-2" />

                <div>
                  <div className="flex items-center justify-between">
                    <h3 className="font-bold text-lg">
                      All Questions Required
                    </h3>
                    <XMarkIcon
                      className="w-[20px] h-[20px] text-[#8C0F0F] cursor-pointer"
                      onClick={() => setUnansweredAlert(false)}
                    />
                  </div>
                  <p className="font-normal !m-0 !mt-1.5 text-base text-[#8C0F0F]">
                    {unansweredAlert}
                  </p>
                </div>
              </div>
            )}

            <div>
              <p className="text-sm text-gray-500 !m-0 !mb-1">
                {examDetail?.category_name}&nbsp;&nbsp;•&nbsp;&nbsp;
                {examDetail?.duration} Mins
              </p>
              <h1 className="text-2xl font-bold text-[#21272A] !m-0">
                {examDetail?.title || "Simulated Exam"}{" "}
                {examState === "submitted" && "(Results)"}
              </h1>
            </div>
            {examState === "in-progress" && (
              <div className="text-right flex items-center border border-[#523C72] bg-[#D9BDFE] px-3 rounded-lg gap-2">
                <p className="text-sm text-[#655B70]">Time left</p>
                <p className={`text-sm font-bold text-[#523C72] ${timerColor}`}>
                  {formatTime(timeLeft)}
                </p>
              </div>
            )}
          </div>

          <div ref={sentinelRef} className="h-[3px]"></div>

          <div className="pt-0">
            {examState === "in-progress" && (
              <div
                className="bg-[rgba(255,204,0,0.15)] p-4 py-3 rounded-xl border border-[#FC0] flex flex-col mb-6"
                role="alert"
              >
                <div className="flex items-center gap-2 font-bold">
                  <InformationCircleIcon className="w-full max-w-[16px] h-[16px] text-[#8F7303]" />
                  <p className="font-bold text-sm text-[#21272A] !m-0">
                    Instruction
                  </p>
                </div>
                <p className="text-sm font-semibold !m-0 !mt-1">
                  Answer the questions as truthfully as possible. You have up to{" "}
                  {examDetail?.duration} minutes. Please do not leave the screen
                  during the exam.
                </p>
              </div>
            )}

            <div className="space-y-8">
              {questions.map((q, index) => {
                const userAnswer = answers[q.id];
                const isCorrect = userAnswer === q.correctAnswer;
                const correctOptionKey = q.correctAnswer;

                return (
                  <div key={q.id}>
                    <div className="flex items-center gap-3">
                      <div className="bg-[#523C72] text-white p-1.5 px-3 !m-0 rounded-lg text-[10px] justify-center items-center flex">
                        {index + 1}
                      </div>
                      <p className="text-sm font-semibold text-[#585F72] my-0 py-0">
                        {q.lesson}
                      </p>
                    </div>
                    <p className="text-base text-[#21272A] my-4 font-semibold">
                      {q.questionText}
                    </p>

                    <div className="space-y-0">
                      {Object.entries(q.options).map(([key, value]) => {
                        const optionId = parseInt(key, 10);
                        const isSelected = userAnswer === optionId;
                        let radioClass =
                          "h-5 w-5 border-gray-400 focus:ring-purple-500";
                        let textClass = "text-[#21272A] text-base";

                        if (examState === "submitted") {
                          if (isSelected && !isCorrect) {
                            radioClass =
                              "h-5 w-5 border-[#B3261E] text-[#B3261E] focus:ring-[#B3261E] accent-[#B3261E]";
                            textClass = "text-[#B3261E]";
                          } else if (optionId === correctOptionKey) {
                            radioClass =
                              "h-5 w-5 border-[#523C72] text-[#523C72] focus:ring-[#523C72] accent-[#523C72]";
                            textClass = "text-[#523C72] font-semibold";
                            // textClass = "text-green-600 font-semibold";
                          }
                        } else if (isSelected) {
                          // Apply the active color when the radio button is selected and the exam is in progress
                          radioClass =
                            "h-5 w-5 border-gray-400 text-[#523C72] focus:ring-[#523C72] accent-[#523C72]";
                          textClass = "text-[#523C72]";
                        }

                        return (
                          <label
                            key={key}
                            className="flex items-center p-2 cursor-pointer rounded-lg hover:bg-gray-50 !m-0 !ml-[-.5rem]"
                          >
                            <input
                              type="radio"
                              name={q.id}
                              value={key}
                              checked={isSelected}
                              onChange={() => {
                                if (examState === "submitted") return;
                                handleAnswerChange(q.id, optionId);
                              }}
                              readOnly={examState === "submitted"}
                              className={radioClass}
                            />
                            <span className={`ml-3 text-base ${textClass}`}>
                              {value}
                            </span>
                          </label>
                        );
                      })}

                      {!answers[q.id] &&
                        isNoAnswerQuestion &&
                        examState === "in-progress" && (
                          <span className="!mt-2  text-base font-medium text-[#8C0F0F] flex items-center gap-1 ">
                            Required to answer
                          </span>
                        )}
                    </div>

                    {examState === "submitted" && (
                      <div className="mt-4 mb-2 p-4  bg-[#FFCC0026]/15 border border-[#FFCC00] rounded-[10px]">
                        <div className="flex items-center gap-2">
                          {/* <FaCheckCircle className="text-green-500" /> */}
                          <p className="font-bold text-[#544522] text-base flex items-center !m-0">
                            {/* <span className="bg-green-500 rounded p-[2px] inline-block mr-2">
                              <CheckIcon className="text-white w-[14px] h-[14px]" />
                            </span>{" "} */}
                            Correct Answer is "{q.options[q.correctAnswer]}"
                          </p>
                        </div>
                        <p className="font-normal text-[#000000] text-base !m-0 !mt-1">
                          <span className="font-normal">Explanation:</span>{" "}
                          {q.explanation}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        {examState === "in-progress" ? (
          <div className="mt-6 w-full border-gray-200">
            <div
              onClick={handleSubmit}
              className="bg-[#007A78] text-center text-white font-bold py-3 px-4 rounded-xl hover:bg-[#005f5e] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-300 cursor-pointer w-full"
            >
              Submit Exam
            </div>
          </div>
        ) : (
          <CourseNavBar
            prevItem={prevItem}
            nextItem={nextItem}
            isEndOfCourse={isEndOfCourse}
            nextCourseId={nextCourseId}
            onEndFallbackRoute="/learn" /* or your track page */
            // showProgress={{ current, total }}
          />
        )}
      </div>
    </>
  );
}
